import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import UndoIcon from '@mui/icons-material/Undo';
import { useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Loading from 'components/Loading';
import React from 'react';
import { i18n } from 'i18n';
import { usePage } from 'providers/page';
import { useLocation } from 'react-router-dom';
import * as THREE from 'three';
import { ProjectModule } from 'providers/api';
import { useProject } from 'modules/project/providers/project';
import { useScenario } from 'modules/project/providers/scenario';
import { useAuthorisation } from 'providers/authorisation';
import { isPcUser, isAdminUser } from 'utils/roles';
import useStopePourTimelines from './hooks/useStopePourTimelines';
import useSceneManager from './hooks/SceneManagerProvider/useSceneManager';
import { ModelState } from './hooks/useModelState';
import { ActionButton } from './Scenes/types';
import Toolbar from './Scenes/BulkEditScene/Toolbar';
import LegendSync from './Components/SceneLegend/LegendSync';
import ToolStatusChip from './Components/ToolStatusChip';
import MeasureController from './Components/MeasureController';
import ThreeCanvas, { SceneControls } from './Canvas';
import NodeForm from './NodeForm';
import SpoolForm from './SpoolForm';
import AutoPourForm from './AutoPourForm';
import AutoPourList from './AutoPourList';
import AutoRecipeForm from './AutoRecipeForm';
import AutoRecipeList from './AutoRecipeList';
import { SceneLegendProvider } from './Components/SceneLegend';
import StopeShapeDateRangeControl from './Components/StopeShapeDateRangeControl';
import { navigateToScene, syncSceneFromPath } from './Scenes/scene-utils';
import { subscribeToBulkImportSolidsSceneState } from './Scenes/BulkImportSolidsScene/bulkImportSolidsSceneState';
import { subscribeToStopeFillSceneState } from './Scenes/StopeFillScene/stopeFillSceneState';
import { getMeasureState, subscribeToMeasureState, deactivateMeasure } from './Scenes/BulkEditScene/Toolbar/measureState';
import { MarqueeProvider } from './contexts/MarqueeContext';
import { ToolHandlerProvider, useToolHandlers } from './contexts/ToolHandlerContext';
import MarqueeOverlay from './EditorPanelPage/components/MarqueeOverlay/MarqueeOverlay';
import { PCNode } from './MineModel/Nodes';
import PCSpool from './MineModel/Spools/PCSpool';
import { IntersectionData } from './MineModel/intersectionTypes';

interface CanvasHandlers {
  onNodeHover: (node: PCNode | null) => void;
  onNodeSelect: (node: PCNode, stopeId?: string) => void;
  onPlantIconSelect?: (node: PCNode) => void;
  onSpoolSelect: (spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => void;
  onStopeShapeSelect?: (stopeId: string) => void;
  onIntersections?: (intersections: IntersectionData[]) => void;
}

interface DispatchedCanvasProps {
  model: ModelState['model'];
  baseHandlers: CanvasHandlers;
  onCameraReady: (camera: THREE.OrthographicCamera, controls: SceneControls) => void;
  onModelTransform: (offset: THREE.Vector3, scale: number) => void;
  zUpMode: boolean;
  onZUpModeChange: (z: boolean) => void;
}

const DispatchedCanvas = ({
  model, baseHandlers, onCameraReady, onModelTransform, zUpMode, onZUpModeChange,
}: DispatchedCanvasProps) => {
  const tools = useToolHandlers();

  const onNodeSelect = React.useCallback((node: PCNode, stopeId?: string) => {
    if (tools.nodeClickHandlerRef.current) {
      tools.nodeClickHandlerRef.current(node);
      return;
    }
    baseHandlers.onNodeSelect(node, stopeId);
  }, [tools, baseHandlers]);

  const onSpoolSelect = React.useCallback((spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => {
    if (tools.spoolClickHandlerRef.current) {
      tools.spoolClickHandlerRef.current(spool, clickPoint);
      return;
    }
    baseHandlers.onSpoolSelect(spool, clickPoint);
  }, [tools, baseHandlers]);

  const onIntersections = React.useCallback((intersections: IntersectionData[]) => {
    if (tools.nodeClickHandlerRef.current) {
      const nodeHit = intersections.find((i) => i.type === 'node' && i.node);
      if (nodeHit?.node) {
        tools.nodeClickHandlerRef.current(nodeHit.node);
        return;
      }
    }
    if (tools.spoolClickHandlerRef.current) {
      const spoolHit = intersections.find((i) => i.type === 'spool' && i.spool);
      if (spoolHit?.spool) {
        tools.spoolClickHandlerRef.current(spoolHit.spool, spoolHit.clickPoint);
        return;
      }
    }
    baseHandlers.onIntersections?.(intersections);
  }, [tools, baseHandlers]);

  return (
    <ThreeCanvas
      model={model}
      onNodeHover={baseHandlers.onNodeHover}
      onNodeSelect={onNodeSelect}
      onPlantIconSelect={baseHandlers.onPlantIconSelect}
      onSpoolSelect={onSpoolSelect}
      onStopeShapeSelect={baseHandlers.onStopeShapeSelect}
      onIntersections={onIntersections}
      onCameraReady={onCameraReady}
      onModelTransform={onModelTransform}
      zUpMode={zUpMode}
      onZUpModeChange={onZUpModeChange}
    />
  );
};

const renderActionButton = (actionButton: ActionButton, model: ModelState['model'], isWhiteBg: boolean) => {
  const selected = typeof actionButton.selected === 'function'
    ? actionButton.selected(model)
    : actionButton.selected;

  const tooltip = typeof actionButton.tooltip === 'function'
    ? actionButton.tooltip(model)
    : actionButton.tooltip;

  const icon = typeof actionButton.icon === 'function'
    ? actionButton.icon(model)
    : actionButton.icon;

  return (
    <Tooltip
      key={actionButton.value}
      title={tooltip}
      placement="right"
    >
      <ToggleButton
        value={actionButton.value}
        aria-label={actionButton.ariaLabel}
        selected={selected}
        onClick={() => actionButton.onClick(model)}
        sx={{
          color: isWhiteBg ? 'black' : 'inherit',
          '&.Mui-selected': { color: isWhiteBg ? 'black' : 'inherit' },
          '&.Mui-selected:hover': { color: isWhiteBg ? 'black' : 'inherit' },
          '&.Mui-disabled': { color: isWhiteBg ? 'rgba(0, 0, 0, 0.4)' : 'inherit' },
        }}
      >
        {icon}
      </ToggleButton>
    </Tooltip>
  );
};

type ButtonChunk =
  | { type: 'single'; button: ActionButton }
  | { type: 'group'; group: string; buttons: ActionButton[] };

const chunkButtonsByGroup = (buttons: ActionButton[]): ButtonChunk[] => buttons.reduce<ButtonChunk[]>((acc, button) => {
  const last = acc[acc.length - 1];
  if (button.group && last && last.type === 'group' && last.group === button.group) {
    last.buttons.push(button);
    return acc;
  }
  if (button.group) {
    acc.push({ type: 'group', group: button.group, buttons: [button] });
    return acc;
  }
  acc.push({ type: 'single', button });
  return acc;
}, []);

const matchesRequiredRoles = (
  requiredRoles: Array<'admin' | 'pc'> | undefined,
  userRoles: string[],
): boolean => {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  return requiredRoles.some((role) => {
    if (role === 'admin') return isAdminUser(userRoles);
    if (role === 'pc') return isPcUser(userRoles);
    return false;
  });
};

const displayActionButton = (
  actionButtons: ActionButton[],
  model: ModelState['model'],
  enabledModules: ProjectModule[],
  userRoles: string[],
  isWhiteBg: boolean,
) => {
  const visibleButtons = actionButtons
    .filter((actionButton) => !actionButton.requiredModule || enabledModules.includes(actionButton.requiredModule))
    .filter((actionButton) => matchesRequiredRoles(actionButton.requiredRoles, userRoles));

  return chunkButtonsByGroup(visibleButtons).map((chunk) => {
    if (chunk.type === 'single') {
      return renderActionButton(chunk.button, model, isWhiteBg);
    }
    if (chunk.buttons.length === 1) {
      return renderActionButton(chunk.buttons[0], model, isWhiteBg);
    }
    return (
      <ToggleButtonGroup
        key={`group-${chunk.group}`}
        orientation="vertical"
      >
        {chunk.buttons.map((b) => renderActionButton(b, model, isWhiteBg))}
      </ToggleButtonGroup>
    );
  });
};

interface StageProps {
  modelState: ModelState;
  refetchMineModel: () => Promise<any>;
}

const Stage = ({ modelState, refetchMineModel }: StageProps) => {
  const sceneManager = useSceneManager();
  const theme = useTheme();
  const { state: pageState } = usePage();
  const { state: { enabledModules } } = useProject();
  const authorisationContext = useAuthorisation();
  const userRoles = authorisationContext.state.user.roles;
  const { activeScenarioId } = useScenario();
  const { model } = modelState;
  const stopePourData = useStopePourTimelines(
    model.project?.entityId,
    activeScenarioId ?? undefined,
    model.showStopeShapes,
  );
  const location = useLocation();
  const modelRef = React.useRef(modelState.model);
  const sceneManagerRef = React.useRef(sceneManager);
  const lastSyncKeyRef = React.useRef<string>('');
  const cameraRef = React.useRef<THREE.OrthographicCamera | null>(null);
  const controlsRef = React.useRef<SceneControls | null>(null);
  const [modelTransform, setModelTransform] = React.useState<{ offset: THREE.Vector3; scale: number } | null>(null);
  const [zUpMode, setZUpMode] = React.useState(false);
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);

  React.useEffect(() => {
    const unsubscribe = subscribeToBulkImportSolidsSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    const unsubscribe = subscribeToStopeFillSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    model.setStopePourData(stopePourData);
  }, [stopePourData, model]);

  React.useEffect(() => subscribeToMeasureState(() => forceUpdate()), []);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!getMeasureState().measureActive) return;
      deactivateMeasure();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCameraReady = React.useCallback((
    camera: THREE.OrthographicCamera,
    controls: SceneControls,
  ) => {
    cameraRef.current = camera;
    controlsRef.current = controls;
  }, []);

  const handleModelTransform = React.useCallback((offset: THREE.Vector3, scale: number) => {
    setModelTransform({ offset, scale });
  }, []);

  React.useEffect(() => {
    modelRef.current = modelState.model;
  }, [modelState.model]);

  React.useEffect(() => {
    sceneManagerRef.current = sceneManager;
  }, [sceneManager]);

  const isModelLoaded = modelState.loaded();
  const syncKey = `${location.pathname}|${isModelLoaded}`;

  React.useEffect(() => {
    if (!sceneManagerRef.current?.actions || modelRef.current.isUpdating) {
      return;
    }

    if (lastSyncKeyRef.current === syncKey) {
      return;
    }

    lastSyncKeyRef.current = syncKey;
    syncSceneFromPath(location.pathname, modelRef.current, sceneManagerRef.current);
  }, [location.pathname, syncKey]);

  if (!sceneManager?.state.currentScene || !sceneManager?.actions) {
    return <Loading />;
  }

  const canvasHandlers = sceneManager.state.currentScene.getCanvasHandlers
    ? sceneManager.state.currentScene.getCanvasHandlers(modelState.model)
    : { onNodeHover: () => { }, onNodeSelect: () => { }, onPlantIconSelect: undefined, onSpoolSelect: () => { }, onIntersections: undefined };
  const canvasHandlersWithStopeShapes = {
    ...canvasHandlers,
    onStopeShapeSelect: canvasHandlers.onStopeShapeSelect ?? ((stopeId: string) => {
      const targetScene = enabledModules.includes(ProjectModule.StopeFill) ? 'STOPE_FILL' : 'STOPE_SELECTED';
      navigateToScene(targetScene, {
        model: modelState.model,
        context: sceneManager,
        stopeId,
      });
    }),
  };
  const isUndoDisabled = !pageState.undoAction.enabled || pageState.undoAction.loading || !pageState.undoAction.onClick;
  const isUndoLoading = pageState.undoAction.loading;
  const isWhiteBg = getMeasureState().backgroundColour === '#ffffff';

  return (
    <ToolHandlerProvider>
      <MarqueeProvider cameraRef={cameraRef} controlsRef={controlsRef} modelTransform={modelTransform}>
        <SceneLegendProvider>
          <LegendSync model={modelState.model} context={sceneManager} />
          <MeasureController model={modelState.model} />
          <Box sx={{ position: 'relative', width: '100%', height: '100%', pt: 'var(--stopes-toolbar-height, 0px)' }}>
            <Toolbar
              model={modelState.model}
              zUpMode={zUpMode}
              onZUpModeChange={setZUpMode}
              context={sceneManager}
            />
            <ToolStatusChip />
            <Box sx={{
              position: 'absolute',
              left: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 100,
            }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'start',
                  gap: 1,
                }}
              >
                {displayActionButton(sceneManager.state.currentScene.actionButtons, modelState.model, enabledModules, userRoles, isWhiteBg)}
                <Tooltip title={i18n._('Undo')} placement="right">
                  <ToggleButton
                    value="global-undo"
                    aria-label="Undo"
                    disabled={isUndoDisabled}
                    onClick={pageState.undoAction.onClick ?? undefined}
                    sx={{
                      color: isWhiteBg ? 'black' : 'inherit',
                      '&.Mui-selected': { color: isWhiteBg ? 'black' : 'inherit' },
                      '&.Mui-selected:hover': { color: isWhiteBg ? 'black' : 'inherit' },
                      '&.Mui-disabled': { color: isWhiteBg ? 'rgba(0, 0, 0, 0.4)' : 'inherit' },
                    }}
                  >
                    <UndoIcon />
                  </ToggleButton>
                </Tooltip>
              </Box>
            </Box>

            {sceneManager.state.currentScene.key !== 'STOPE_FILL' && (
              <StopeShapeDateRangeControl model={modelState.model} />
            )}

            <Box>
              {sceneManager.actions.sideDrawerContent && sceneManager.actions.sideDrawerContent(modelState.model)}
            </Box>

            <Box sx={{ position: 'fixed', left: 0, top: 0, width: '100%', height: '100%', zIndex: 0 }}>
              <DispatchedCanvas
                model={modelState.model}
                baseHandlers={canvasHandlersWithStopeShapes}
                onCameraReady={handleCameraReady}
                onModelTransform={handleModelTransform}
                zUpMode={zUpMode}
                onZUpModeChange={setZUpMode}
              />
            </Box>
            <MarqueeOverlay model={modelState.model} />

            <Box>
              {sceneManager.actions.display && sceneManager?.actions.display(modelState.model)}
            </Box>

            <Backdrop
              open={isUndoLoading}
              sx={{
                color: theme.palette.common.white,
                zIndex: theme.zIndex.modal + 1,
              }}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </Box>
          {sceneManager?.state.shouldEditNode && modelState.model.selectedNode && (
            <NodeForm
              model={modelState.model}
              nodeId={modelState.model.selectedNode.id}
              addStopeMode={sceneManager.state.addStopeMode}
              onComplete={async (shouldRefresh = false) => {
                sceneManager.actions.editNodes(false);
                sceneManager.actions.setAddStopeMode(false);

                if (shouldRefresh) {
                  const newMineModel = await refetchMineModel();

                  modelState.initModelState(
                    modelState.model.project,
                    newMineModel.data,
                    modelState.model.mineOverview,
                  );

                  sceneManager.actions.refreshScene();
                }
              }}
            />
          )}
          {sceneManager?.state.spoolToEdit && (
            <SpoolForm
              key={sceneManager.state.spoolToEdit.spoolId}
              model={modelState.model}
              spool={sceneManager.state.spoolToEdit}
              onComplete={() => sceneManager.actions.editSpool(undefined)}
            />
          )}
          {sceneManager?.state.shouldAutoRecipeCreation && (
            <AutoRecipeForm
              modelState={modelState}
              onComplete={() => sceneManager.actions.setAutoRecipeCreationOpen(false)}
            />
          )}
          {sceneManager?.state.shouldAutoRecipeList && (
            <AutoRecipeList
              model={modelState.model}
              project={modelState.model.project}
              onComplete={() => sceneManager.actions.setAutoRecipeListOpen(false)}
            />
          )}
          {sceneManager?.state.shouldAutoPourCreation && (
            <AutoPourForm
              modelState={modelState}
              onComplete={() => sceneManager.actions.setAutoPourCreationOpen(false)}
            />
          )}
          {sceneManager?.state.shouldAutoPourList && (
            <AutoPourList
              model={modelState.model}
              project={modelState.model.project}
              onComplete={() => sceneManager.actions.setAutoPourListOpen(false)}
            />
          )}
        </SceneLegendProvider>
      </MarqueeProvider>
    </ToolHandlerProvider>
  );
};
export default Stage;
